import { IconX } from "@tabler/icons-react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { useLocalize } from "../../content/useLocalize";
import { connectedNodes } from "../../engine/networkGraph";
import type { ExternalAccess } from "../../engine/toolHints";
import { nodeTypeText, recommendedActionText } from "../../i18n/enumText";
import type { NetworkNode, RecommendedActionCode } from "../../types";
import { RecommendedActionSelect } from "../common/RecommendedActionSelect";
import { cx } from "../common/cx";
import { NodeIcon, RISK_CLASS, VERDICT_VIEW, verdictFor } from "./nodeView";
import { riskTintClass } from "./riskTint";
import styles from "./NodeDetailPanel.module.css";

const measureLabel = msg`Защитная мера`;

export interface NodeDetailPanelProps {
  node: NetworkNode;
  nodes: readonly NetworkNode[];
  isMarked: boolean;
  measure: RecommendedActionCode | undefined;
  submitted: boolean;
  /** Recommended measure revealed by the Network Security skill (Lv2/Lv3). */
  revealedMeasure?: RecommendedActionCode;
  /** How the outside reaches this node (access_matrix_viewer); undefined when the tool is off. */
  externalAccess?: ExternalAccess;
  onToggle: () => void;
  onPickMeasure: (code: RecommendedActionCode) => void;
  onClose: () => void;
}

export function NodeDetailPanel({
  node,
  nodes,
  isMarked,
  measure,
  submitted,
  revealedMeasure,
  externalAccess,
  onToggle,
  onPickMeasure,
  onClose,
}: NodeDetailPanelProps): JSX.Element {
  const { i18n } = useLingui();
  const t = useLocalize();
  const links = connectedNodes(node, nodes);
  const verdict = verdictFor(node, isMarked, measure);

  return (
    <div className={cx(styles.panel, submitted && styles[verdict])}>
      <div className={styles.detailHead}>
        <span className={styles.nodeIco}>
          <NodeIcon type={node.type} size={18} />
        </span>
        <div className={styles.titleWrap}>
          <span className={styles.title}>{i18n._(nodeTypeText(node.type))}</span>
          <span className={cx(styles.host, "mono")}>{node.id}</span>
        </div>
        <button
          type="button"
          className={styles.detailClose}
          aria-label={i18n._(msg`Снять выбор`)}
          onClick={onClose}
        >
          <IconX size={16} aria-hidden="true" />
        </button>
      </div>

      {!submitted && (
        <label className={cx(styles.flagToggle, isMarked && styles.flagToggleOn)}>
          <input type="checkbox" checked={isMarked} disabled={submitted} onChange={onToggle} />
          <Trans>Пометить как рискованный</Trans>
        </label>
      )}

      <div className={styles.detailBlock}>
        <span className={styles.detailLabel}>
          <Trans>Чем рискует</Trans>
        </span>
        <p className={styles.detailWhy}>{t(node.riskRationale)}</p>
      </div>

      {isMarked && !submitted && (
        <div className={styles.detailBlock}>
          <span className={styles.detailLabel}>
            <Trans>Мера</Trans>
          </span>
          <RecommendedActionSelect
            className={styles.select}
            value={measure ?? ""}
            ariaLabel={measureLabel}
            disabled={submitted}
            onChange={onPickMeasure}
          />
          {revealedMeasure && (
            <span className={styles.answer}>
              <Trans>Навык подсказывает:</Trans> {i18n._(recommendedActionText(revealedMeasure))}
            </span>
          )}
        </div>
      )}

      {externalAccess && (
        <div className={styles.detailBlock}>
          <span className={styles.detailLabel}>
            <Trans>Доступ снаружи</Trans>
          </span>
          {externalAccess.kind === "direct" && (
            <p className={cx(styles.detailWhy, styles.accessDirect)}>
              <Trans>Доступен из интернета напрямую</Trans>
            </p>
          )}
          {externalAccess.kind === "unreachable" && (
            <p className={styles.detailWhy}>
              <Trans>Снаружи не достижим</Trans>
            </p>
          )}
          {externalAccess.kind === "path" && (
            <div className={styles.connChips}>
              <span className={styles.connChip}>
                <Trans>интернет</Trans>
              </span>
              {externalAccess.via.map((id) => {
                const step = nodes.find((n) => n.id === id);
                return (
                  <span key={id} className={cx(styles.connChip, step && riskTintClass(RISK_CLASS, step.riskLevel, submitted))}>
                    → {step ? i18n._(nodeTypeText(step.type)) : id}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {links.length > 0 && (
        <div className={styles.detailBlock}>
          <span className={styles.detailLabel}>
            <Trans>Связи</Trans>
          </span>
          <div className={styles.connChips}>
            {links.map((c) => (
              <span key={c.id} className={cx(styles.connChip, riskTintClass(RISK_CLASS, c.riskLevel, submitted))}>
                {i18n._(nodeTypeText(c.type))} <span className="mono">{c.id}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {submitted && (
        <div className={styles.feedback}>
          <span className={styles.verdict}>
            {VERDICT_VIEW[verdict].icon}
            {VERDICT_VIEW[verdict].label}
          </span>
          {node.isRisky && node.recommendedMeasure && (
            <span className={styles.answer}>
              <Trans>Рекомендация:</Trans>{" "}
              {i18n._(recommendedActionText(node.recommendedMeasure))}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
